import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useNavigationType } from 'react-router';
import { useMessage } from '@tmapy/intl';
import { useDispatch, useSelector } from '@tmapy/redux';

import { WaitingAlert, DangerAlert, InfoAlert, Table } from '@tmapy/style-guide';

import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { FilterBtn } from '../../app/components/FilterBtn';
import { useUsersListResource } from '../../openapi/operations/usersList';
import { QUERY_STATE } from '../../constants';
import { UserListItem } from './UserListItem';
import { UserFilter } from './UserFilter';

export const msg = defineMessages({
  userListFiltrLabel: {
    id: 'userList.label.filter',
    defaultMessage: 'Filtr',
  },
  userListPageTitle: {
    id: 'userList.PageTitle',
    defaultMessage: 'Přehled uživatelů',
  },
  userListStateWaiting: {
    id: 'userList.state.waiting',
    defaultMessage: 'Seznam uživatelů se načítá.',
  },
  userListStateFail: {
    id: 'userList.state.fail',
    defaultMessage: 'Seznam uživatelů se nepovedlo načíst.',
  },
  userListStateEmpty: {
    id: 'userList.state.empty',
    defaultMessage: 'Seznam uživatelů je prázdný.',
  },
  userListStateFilteredEmpty: {
    id: 'userList.state.filteredEmpty',
    defaultMessage: 'Zadanému filtru neodpovídá žádný záznam.',
  },
  userListUser: {
    id: 'userList.user',
    defaultMessage: 'Uživatel',
  },
  userListEmail: {
    id: 'userList.email',
    defaultMessage: 'E-mail',
  },
  userListOrganization: {
    id: 'userList.organization',
    defaultMessage: 'Organizace',
  },
  userListDateJoind: {
    id: 'userList.dateJoined',
    defaultMessage: 'Vytvoření uživatele',
  },
  userListLastLogin: {
    id: 'userList.lastLogin',
    defaultMessage: 'Poslední přihlášení',
  },
});

export const UserList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.userList);

  const query: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  const { fetchMore, queryState, data } = useUsersListResource(query, navigationType !== 'PUSH');

  const userList = data?.results ?? [];
  const userCount = data?.count;

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  function handleNextUsersBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(msg.userListFiltrLabel)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='userlist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-fw-w sg-a-cg-1 sg-a-rg-1'>
            <PageTitle>
              <FormattedMessage {...msg.userListPageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />
            <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
          </header>
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.userListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.userListStateFail} />
            </DangerAlert>
          )}
          {queryState === QUERY_STATE.SUCCESS && userList.length < 1 && (
            <>
              {isFiltered ? (
                <InfoAlert>
                  <FormattedMessage {...msg.userListStateFilteredEmpty} />
                </InfoAlert>
              ) : (
                <InfoAlert>
                  <FormattedMessage {...msg.userListStateEmpty} />
                </InfoAlert>
              )}
            </>
          )}
          {userList.length > 0 && (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...msg.userListUser} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.userListEmail} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.userListOrganization} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.userListDateJoind} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.userListLastLogin} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userList.map((userItem) => (
                      <UserListItem key={userItem.id} {...userItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={userList.length}
                total={userCount!}
                queryState={queryState}
                onNextBtnClick={handleNextUsersBtnClick}
              />
            </div>
          )}
        </article>
      </MainContent>
      <UserFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
